import { Link } from "react-router-dom";
import { Facebook, Phone, Mail, MapPin } from "lucide-react";
import { BUSINESS, SERVICES, SUBURBS, SUBURB_PAGES } from "../content";
import { Logo } from "./Logo";
import { track } from "../lib/track";

export const Footer = () => (
  <footer data-testid="site-footer" className="bg-brand-deep text-teal-50">
    <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-20">
      <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <Logo light />
          <p className="mt-5 max-w-xs text-sm leading-relaxed text-teal-100/80">
            Reliable, detailed cleaning for homes, rental properties and businesses across {BUSINESS.region}.
          </p>
          <a
            href={BUSINESS.facebook}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-facebook-link"
            onClick={() => track("social_click", { network: "facebook", location: "footer" })}
            className="mt-5 inline-flex items-center gap-2 rounded-full border border-teal-300/30 px-4 py-2 text-sm font-semibold text-teal-100 transition-colors hover:bg-white/10"
          >
            <Facebook className="h-4 w-4" /> Follow us on Facebook
          </a>
        </div>
        <nav aria-label="Services">
          <h3 className="font-display text-sm font-bold uppercase tracking-widest text-teal-200">Services</h3>
          <ul className="mt-4 space-y-2.5 text-sm">
            {SERVICES.map((s) => (
              <li key={s.name}>
                <Link to={s.to} className="text-teal-100/80 transition-colors hover:text-white">{s.name}</Link>
              </li>
            ))}
          </ul>
        </nav>
        <nav aria-label="Service areas">
          <h3 className="font-display text-sm font-bold uppercase tracking-widest text-teal-200">Service Areas</h3>
          <ul className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
            {SUBURBS.slice(0, 10).map((s) => {
              const page = SUBURB_PAGES.find((p) => p.suburb === s);
              return (
                <li key={s}>
                  <Link to={page ? `/${page.slug}` : "/service-areas"} className="text-teal-100/80 transition-colors hover:text-white">{s}</Link>
                </li>
              );
            })}
          </ul>
          <Link to="/service-areas" className="mt-3 inline-block text-sm font-semibold text-teal-200 underline-offset-4 hover:underline">
            View all areas
          </Link>
        </nav>
        <div>
          <h3 className="font-display text-sm font-bold uppercase tracking-widest text-teal-200">Get in Touch</h3>
          <ul className="mt-4 space-y-3 text-sm">
            <li>
              <a href={BUSINESS.phoneHref} data-testid="footer-phone-link" onClick={() => track("click_to_call", { location: "footer" })} className="flex items-center gap-2.5 text-teal-100/80 transition-colors hover:text-white">
                <Phone className="h-4 w-4 shrink-0 text-teal-300" /> {BUSINESS.phone}
              </a>
            </li>
            {BUSINESS.email && (
              <li>
                <a href={`mailto:${BUSINESS.email}`} data-testid="footer-email-link" onClick={() => track("email_click", { location: "footer" })} className="flex items-center gap-2.5 text-teal-100/80 transition-colors hover:text-white">
                  <Mail className="h-4 w-4 shrink-0 text-teal-300" /> {BUSINESS.email}
                </a>
              </li>
            )}
            <li className="flex items-start gap-2.5 text-teal-100/80">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-teal-300" />
              Servicing {BUSINESS.baseSuburb} and surrounding {BUSINESS.region} suburbs
            </li>
          </ul>
          <Link
            to="/contact"
            data-testid="footer-quote-button"
            className="mt-6 inline-flex rounded-full bg-white px-6 py-3 text-sm font-bold text-brand-deep transition-transform duration-200 hover:-translate-y-0.5"
          >
            Get a Free Quote
          </Link>
        </div>
      </div>
      <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-8 text-xs text-teal-100/60 sm:flex-row sm:items-center">
        <p>© {new Date().getFullYear()} {BUSINESS.name}. All rights reserved.</p>
        <div className="flex gap-6">
          <Link to="/privacy-policy" className="transition-colors hover:text-white">Privacy Policy</Link>
          <Link to="/contact" className="transition-colors hover:text-white">Contact</Link>
        </div>
      </div>
    </div>
  </footer>
);
